import type { Music24Screen } from '../../../../../music24-api/src/productEvents/music24EventCatalog';
import { extractLocationFromPath } from '../../../routing/sml/extractLocationFromPath';
import { SMLRoutes } from '../../../routing/sml/SMLRoutes';

/**
 * Mirrors `MUSIC24_SCREENS` of the events catalog. The web build cannot import a value from
 * music24-api (CRA allows imports in `src/` only); the record type makes tsc fail on any drift.
 */
const SCREEN_NAMES: Record<Music24Screen, true> = {
  home: true,
  welcome: true,
  addSong: true,
  watchlist: true,
  trends: true,
  markets: true,
  artists: true,
  artistDetails: true,
  tracks: true,
  trackDetails: true,
  playlists: true,
  playlistDetails: true,
  browse: true,
  streaming: true,
  social: true,
  fans: true,
  smartlinks: true,
  catalog: true,
  catalogSong: true,
  catalogLyrics: true,
  coverEditor: true,
  coverMaker: true,
  musicVideoDemo: true,
  auth: true,
  account: true,
  create: true,
  edit: true,
  duplicate: true,
  details: true,
  manage: true,
  widgetEdit: true,
  releaseLink: true,
  playlistLink: true,
  presaveLink: true,
  trackers: true,
  trackerEdit: true,
  promote: true,
  promoteCalendar: true,
  promoteChannels: true,
  promoteLibrary: true,
  promoteRelease: true,
  promoteStudio: true,
  promoteProfile: true,
  promoteWork: true,
  promoteAssets: true,
  promotePosts: true,
  promoteComposer: true,
  outreach: true,
  outreachProfile: true,
  outreachFind: true,
  outreachCommunities: true,
  outreachContacts: true,
  outreachInbox: true,
  outreachCampaignNew: true,
  outreachCampaign: true,
  outreachReview: true,
  settings: true,
  paymentComplete: true,
  oauthConsent: true,
  teamAccept: true,
  public: true,
  a: true,
  publicHub: true,
  yoursHub: true,
  promoteHub: true,
  outreachHub: true,
  other: true,
};
const SCREENS: ReadonlySet<string> = new Set(Object.keys(SCREEN_NAMES));

/** Paths whose screen the route table does not name. The first match wins. */
const SPECIAL: [RegExp, Music24Screen][] = [
  [/^\/?catalog\/song\/[^/]+\/cover\/?$/, 'coverEditor'],
  [/^\/?catalog\/song\/[^/]+\/?/, 'catalogSong'],
  [/^\/?catalog\/connect\//, 'catalog'],
  [/^\/?cover-maker/, 'coverMaker'],
  [/^\/?music-video-demo/, 'musicVideoDemo'],
  [/^\/?oauth-consent/, 'oauthConsent'],
  [/^\/?team\/accept/, 'teamAccept'],
  [/^\/?a\/[^/]+/, 'a'],
];

/**
 * The screen name of a path of the web app (the router path, without `/app`): the route name of
 * `SMLRouteNames`, else a special name, else `other`. It never holds an id of the path.
 */
export function screenOfPath(pathname: string): Music24Screen {
  const path = pathname.replace(/^\/app(?=\/|$)/, '');
  for (const [pattern, screen] of SPECIAL) {
    if (pattern.test(path)) {
      return screen;
    }
  }
  const route = extractLocationFromPath(path);
  if (route !== undefined) {
    const entry = Object.entries(SMLRoutes).find(([, parts]) => parts === route);
    if (entry !== undefined && SCREENS.has(entry[0])) {
      return entry[0] as Music24Screen;
    }
  }
  return path === '' || path === '/' ? 'home' : 'other';
}
